import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../slices/authSlice";
import { FaBars, FaTimes } from "react-icons/fa";
import axios from "axios";
import config from "../../config";
import { setPaymentData } from "../../slices/paymentSlice";
import { removeFromCart, clearCart } from "../../slices/cartSlice";
import "./navbar.css";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(false);
  const navRef = useRef(null);
  const cartRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authToken = useSelector((state) => state.auth.token);
  const deviceId = useSelector((state) => state.auth.deviceId);
  const eventId = useSelector((state) => state.auth.eventId);
  const applicantId = useSelector((state) => state.auth.applicantId) || 0;
  const userId = useSelector((state) => state.auth.userId) || "";
  const cart = useSelector((state) => state.cart.items);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleCartVisibility = () => {
    setShowCart(!showCart);
  };

  const handleClickOutside = (event) => {
    if (cartRef.current && !cartRef.current.contains(event.target)) {
      setShowCart(false);
    }
  };

  useEffect(() => {
    if (showCart) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCart]);

  const handleSignOut = () => {
    dispatch(logout());
    navigate("/login");
  };

  const handleProceedToPayment = async () => {
    if (cart.length === 0) {
      alert("Your cart is empty.");
      return;
    }
    setLoading(true);
    const workshopIDs = cart.map((item) => item.WorkshopID).join(",");
    const checkoutUrl = config.API_ADD_TO_ORDER_URL.replace(
      "{DeviceID}",
      deviceId
    )
      .replace("{UserID}", encodeURIComponent(userId))
      .replace("{workshopIDs}", workshopIDs)
      .replace("{ApplicantID}", applicantId)
      .replace("{EventID}", eventId);
    try {
      const response = await fetch(checkoutUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error("Checkout API call failed");
      }
      const data = await response.json();
      if (data.Table && data.Table.length > 0) {
        const unavailable = data.Table.filter(
          (item) => item.Status === "Seats not available"
        );
        if (unavailable.length > 0) {
          alert(
            "No seats available for: " +
              unavailable.map((u) => u.Description.trim()).join(", ")
          );
          setLoading(false);
          return;
        }
        if (data.Table[0].Status === "True") {
          const { "Payment Code": paymentCode } = data.Table[0];
          const paymentPayload = {
            orderID: paymentCode,
            returnPath: "https://i2.sihs.org.pk/paymentconfirm.aspx",
          };

          const initiateResponse = await axios.post(
            config.API_INITIATE_PAYMENT_URL,
            paymentPayload,
            {
              headers: { "Content-Type": "application/json" },
            }
          );
          console.log("Initiate Payment Response:", initiateResponse.data);
          if (
            initiateResponse.data.Transaction &&
            initiateResponse.data.Transaction.PaymentPortal
          ) {
            dispatch(
              setPaymentData({
                transactionID: initiateResponse.data.Transaction.TransactionID,
                paymentPortal: initiateResponse.data.Transaction.PaymentPortal,
              })
            );
            dispatch(clearCart());
            window.location.href =
              initiateResponse.data.Transaction.PaymentPortal;
          } else {
            alert("Payment registration failed.");
            setLoading(false);
          }
        } else {
          alert("Unexpected response from checkout API.");
          setLoading(false);
        }
      } else {
        alert("Unexpected response from checkout API.");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error during checkout:", error);
      alert("An error occurred during checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="navbar">
      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Please wait...</p>
        </div>
      )}
      <div className="hamburger" onClick={toggleMenu}>
        {isOpen ? <FaTimes /> : <FaBars />}
      </div>
      <div className={`nav-links ${isOpen ? "open" : ""}`} ref={navRef}>
        <NavLink to="/" end className="nav-link" onClick={toggleMenu}>
          Home
        </NavLink>
        {authToken && (
          <NavLink to="/profile" className="nav-link" onClick={toggleMenu}>
            Profile
          </NavLink>
        )}
        {/* <NavLink to="/abstract-approval" className="nav-link" onClick={toggleMenu}>
          Abstract Review
        </NavLink> */}
        <NavLink to="/program" className="nav-link" onClick={toggleMenu}>
          Program
        </NavLink>
        <NavLink to="/speakers" className="nav-link" onClick={toggleMenu}>
          Speakers
        </NavLink>
        <NavLink to="/messages" className="nav-link" onClick={toggleMenu}>
          Messages
        </NavLink>
        <NavLink to="/workshops" className="nav-link" onClick={toggleMenu}>
          Workshops
        </NavLink>
        <NavLink to="/posterlist" className="nav-link" onClick={toggleMenu}>
          Approved Poster List
        </NavLink>
        <NavLink to="/archive" className="nav-link" onClick={toggleMenu}>
          Archives
        </NavLink>
        <NavLink to="/contact" className="nav-link" onClick={toggleMenu}>
          Contact Us
        </NavLink>
      </div>
      <div className="navbar-actions">
        {authToken ? (
          <>
            <div className="navbar-cart">
              <div className="cart-icon" onClick={toggleCartVisibility}>
                🛒{" "}
                {cart.length > 0 && (
                  <span className="cart-count">{cart.length}</span>
                )}
              </div>
              {showCart && (
                <div className="cart-panel" ref={cartRef}>
                  <h2>Your Cart</h2>
                  {cart.length === 0 ? (
                    <p>No workshops added.</p>
                  ) : (
                    <ul className="cart-list">
                      {cart.map((item, index) => (
                        <li
                          key={`${item.WorkshopID}-${index}`}
                          className="cart-item"
                        >
                          <span className="cart-item-title">{item.Topic}</span>
                          <span className="cart-item-price">
                            RS: {item.Price}
                          </span>
                          <button
                            onClick={() =>
                              dispatch(removeFromCart(item.WorkshopID))
                            }
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {cart.length > 0 && (
                    <div className="cart-summary">
                      <div className="total-price">
                        Total: RS:{" "}
                        {cart.reduce(
                          (sum, item) => sum + Number(item.Price),
                          0
                        )}
                      </div>
                      <button
                        className="proceed-button"
                        onClick={handleProceedToPayment}
                      >
                        Proceed to Payment
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              className="signout-button"
              onClick={() => {
                handleSignOut();
              }}
            >
              Sign Out
            </button>
          </>
        ) : (
          <NavLink to="/login" className="nav-link">
            Login
          </NavLink>
        )}
      </div>
    </div>
  );
};

export default Navbar;
